/**
 * Image utility functions for responsive images and Cloudinary transformations
 */

import type { ImageResponse } from "../api/generated/types.gen";

/**
 * Get image ID from an image object.
 * Uses the id field if available, otherwise falls back to cloudinary_public_id.
 *
 * @param image - Image object (ImageResponse or similar)
 * @returns Image ID string
 */
export function getImageId(
  image: ImageResponse | { id?: string | null; cloudinary_public_id?: string }
): string {
  return image.id || image.cloudinary_public_id || "";
}

/**
 * Format a date string to a readable format.
 *
 * @param dateString - ISO date string or null/undefined
 * @returns Formatted date string or empty string if date is invalid
 */
export function formatDate(dateString: string | null | undefined): string {
  if (!dateString) {
    return "";
  }

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return "";
    }
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return "";
  }
}

/**
 * Check if browser supports modern image formats
 * @returns Object with format support flags
 */
export function getBrowserImageFormatSupport(): {
  webp: boolean;
  avif: boolean;
} {
  if (typeof window === "undefined") {
    return { webp: false, avif: false };
  }

  const canvas = document.createElement("canvas");
  canvas.width = 1;
  canvas.height = 1;

  return {
    webp:
      canvas.toDataURL("image/webp").indexOf("data:image/webp") === 0,
    avif:
      canvas.toDataURL("image/avif").indexOf("data:image/avif") === 0,
  };
}

/**
 * Get optimal image format for current browser
 * @param preferAvif - Whether to prefer AVIF over WebP (default: true)
 * @returns Format string for Cloudinary (f_avif, f_webp, or f_auto)
 */
export function getOptimalImageFormat(preferAvif: boolean = true): string {
  if (typeof window === "undefined") {
    return "f_auto"; // Server-side: let Cloudinary decide
  }

  const support = getBrowserImageFormatSupport();
  
  if (preferAvif && support.avif) {
    return "f_avif";
  }
  if (support.webp) {
    return "f_webp";
  }
  return "f_auto"; // Fallback to Cloudinary auto-detection
}

/**
 * Generate Cloudinary transformation URL with optimal format
 * @param baseUrl - Original Cloudinary URL
 * @param transformations - Cloudinary transformation string (e.g., "w_800,h_600,c_fill,q_auto")
 * @param useModernFormats - Whether to use WebP/AVIF formats (default: true)
 * @returns Transformed URL
 */
export function transformCloudinaryUrl(
  baseUrl: string,
  transformations: string,
  useModernFormats: boolean = true
): string {
  if (!baseUrl.includes("cloudinary.com")) {
    return baseUrl;
  }

  // If transformations already include format, use as-is
  if (transformations.includes("f_")) {
    return baseUrl.replace("/upload/", `/upload/${transformations}/`);
  }

  // Add optimal format if not specified and modern formats are enabled
  if (useModernFormats) {
    const optimalFormat = getOptimalImageFormat();
    // Only add format if it's not already f_auto (to avoid redundancy)
    if (optimalFormat !== "f_auto") {
      transformations = `${transformations},${optimalFormat}`;
    } else {
      transformations = `${transformations},f_auto`;
    }
  } else {
    transformations = `${transformations},f_auto`;
  }

  return baseUrl.replace("/upload/", `/upload/${transformations}/`);
}

/**
 * Generate srcset for responsive images using Cloudinary with modern formats
 * @param baseUrl - Original Cloudinary URL
 * @param sizes - Array of widths in pixels
 * @param useModernFormats - Whether to use WebP/AVIF formats (default: true)
 * @returns srcset string
 */
export function generateSrcSet(
  baseUrl: string,
  sizes: number[],
  useModernFormats: boolean = true
): string {
  if (!baseUrl.includes("cloudinary.com")) {
    return baseUrl; // Return original if not Cloudinary
  }

  const optimalFormat = useModernFormats ? getOptimalImageFormat() : "f_auto";

  return sizes
    .map((width) => {
      const transformedUrl = transformCloudinaryUrl(
        baseUrl,
        `w_${width},q_auto,${optimalFormat}`,
        false // Don't add format again since we're specifying it
      );
      return `${transformedUrl} ${width}w`;
    })
    .join(", ");
}

/**
 * Generate sizes attribute for responsive images
 * @param breakpoints - Object with breakpoint names and sizes
 * @returns sizes string
 */
export function generateSizes(breakpoints: {
  mobile?: string;
  tablet?: string;
  desktop?: string;
  default: string;
}): string {
  const parts: string[] = [];

  if (breakpoints.mobile) {
    parts.push(`(max-width: 600px) ${breakpoints.mobile}`);
  }
  if (breakpoints.tablet) {
    parts.push(`(max-width: 960px) ${breakpoints.tablet}`);
  }
  if (breakpoints.desktop) {
    parts.push(`(max-width: 1280px) ${breakpoints.desktop}`);
  }
  parts.push(breakpoints.default);

  return parts.join(", ");
}

/**
 * Get responsive image attributes for Cloudinary images with modern format support
 * @param baseUrl - Original Cloudinary URL
 * @param options - Configuration options
 * @returns Object with src, srcSet, and sizes
 */
export function getResponsiveImageAttributes(
  baseUrl: string,
  options: {
    widths?: number[];
    sizes?: string;
    defaultSize?: number;
    useModernFormats?: boolean;
  } = {}
): {
  src: string;
  srcSet?: string;
  sizes?: string;
} {
  const widths = options.widths || [400, 800, 1200, 1600, 2000];
  const defaultSize = options.defaultSize || 1200;
  const useModernFormats = options.useModernFormats !== false; // Default to true

  const optimalFormat = useModernFormats ? getOptimalImageFormat() : "f_auto";

  const result: {
    src: string;
    srcSet?: string;
    sizes?: string;
  } = {
    src: transformCloudinaryUrl(
      baseUrl,
      `w_${defaultSize},q_auto,${optimalFormat}`,
      false // Don't add format again
    ),
  };

  if (baseUrl.includes("cloudinary.com")) {
    result.srcSet = generateSrcSet(baseUrl, widths, useModernFormats);
    result.sizes = options.sizes || "100vw";
  }

  return result;
}
